/**
 * Booqable Add to Cart - Custom Element
 * Handles adding products to the Booqable rental cart
 * 
 * @element booqable-add-to-cart
 * @fires booqable:added - Dispatched when item is added to cart
 * @fires booqable:error - Dispatched when add fails
 */

// Uses window.BooqableConfig from booqable-config.js

class BooqableAddToCart extends HTMLElement {
    constructor() {
        super();
        // Regular instance properties (not private fields for better compatibility)
        this.productId = null;
        this.minOrderQuantity = 1;
        this.quantity = 1;
        this.isLoading = false;
        this.apiBaseUrl = null;
        this.cachedCartId = null; // Store cart ID from API response for subsequent requests

        // DOM references
        this.submitButton = null;
        this.quantityInput = null;
        this.decreaseBtn = null;
        this.increaseBtn = null;
    }

    /**
     * Called when element is added to DOM
     */
    connectedCallback() {
        console.log('[BooqableAddToCart] Connected');

        this.productId = this.getAttribute('product-id');
        this.minOrderQuantity = parseInt(this.getAttribute('min-order-quantity'), 10) || 1;
        this.quantity = this.minOrderQuantity;
        console.log('[BooqableAddToCart] Product ID:', this.productId, 'Min order qty:', this.minOrderQuantity);

        if (!this.productId) {
            console.warn('[BooqableAddToCart] No product ID, hiding element');
            this.style.display = 'none';
            return;
        }

        // Save min order quantity mapping for cart drawer to use
        this.saveMinOrderQuantity(this.productId, this.minOrderQuantity);

        const slug = window.booqableOptions?.slug || window.BooqableConfig?.slug || 'the-social-kitchen';
        this.apiBaseUrl = window.BooqableConfig?.apiBaseUrl || `https://${slug}.booqableshop.com/api/1`;

        this.bindElements();
        this.bindEvents();

        console.log('[BooqableAddToCart] Initialized successfully');
    }

    /**
     * Bind DOM element references
     */
    bindElements() {
        this.submitButton = this.querySelector('[data-booqable-submit]');
        this.quantityInput = this.querySelector('[data-booqable-quantity]');
        this.decreaseBtn = this.querySelector('[data-action="decrease"]');
        this.increaseBtn = this.querySelector('[data-action="increase"]');

        console.log('[BooqableAddToCart] Elements bound:', {
            submitButton: !!this.submitButton,
            quantityInput: !!this.quantityInput,
            decreaseBtn: !!this.decreaseBtn,
            increaseBtn: !!this.increaseBtn
        });
    }

    /**
     * Bind event listeners
     */
    bindEvents() {
        const self = this;

        // Add to cart button
        if (this.submitButton) {
            this.submitButton.addEventListener('click', function (e) {
                e.preventDefault();
                console.log('[BooqableAddToCart] Submit button clicked');
                self.addToCart();
            });
        }

        // Decrease button
        if (this.decreaseBtn) {
            this.decreaseBtn.addEventListener('click', function () {
                console.log('[BooqableAddToCart] Decrease clicked');
                self.updateQuantity(-1);
            });
        }

        // Increase button
        if (this.increaseBtn) {
            this.increaseBtn.addEventListener('click', function () {
                console.log('[BooqableAddToCart] Increase clicked');
                self.updateQuantity(1);
            });
        }

        // Quantity input change
        if (this.quantityInput) {
            this.quantityInput.addEventListener('change', function (e) {
                let value = parseInt(e.target.value, 10);
                var step = self.minOrderQuantity;
                if (isNaN(value) || value < step) {
                    value = step;
                } else {
                    // Snap to nearest valid step
                    value = Math.round(value / step) * step;
                    if (value < step) value = step;
                }
                self.quantity = value;
                e.target.value = value;
                console.log('[BooqableAddToCart] Quantity changed to:', value);
            });
        }

        // Listen for variant changes. Skipped for static instances (e.g. grouped
        // product rows), where multiple components share the page and must keep
        // their own server-rendered product-id rather than syncing to a single
        // global variant:changed event.
        if (!this.hasAttribute('data-static')) {
        document.addEventListener('variant:changed', function (e) {
            const meta = e.detail?.variant?.metafields?.custom;
            const newProductId = meta?.booqable_product_id || meta?.booqable_id;
            if (newProductId) {
                self.productId = newProductId;
                self.setAttribute('product-id', newProductId);
                self.style.display = '';

                // Update min order quantity from new variant
                var newMinQty = parseInt(e.detail?.variant?.metafields?.custom?.min_order_quantity, 10) || 1;
                self.minOrderQuantity = newMinQty;
                self.quantity = newMinQty;
                if (self.quantityInput) {
                    self.quantityInput.value = newMinQty;
                    self.quantityInput.min = newMinQty;
                    self.quantityInput.step = newMinQty;
                }
                self.saveMinOrderQuantity(newProductId, newMinQty);

                console.log('[BooqableAddToCart] Variant changed, product ID:', newProductId, 'min qty:', newMinQty);
            } else {
                self.style.display = 'none';
            }
        });
        }

        console.log('[BooqableAddToCart] Events bound');
    }

    /**
     * Update quantity by step (min_order_quantity)
     */
    updateQuantity(delta) {
        const step = this.minOrderQuantity;
        const newQty = Math.max(step, this.quantity + delta * step);
        this.quantity = newQty;
        if (this.quantityInput) {
            this.quantityInput.value = newQty;
        }
        console.log('[BooqableAddToCart] Quantity updated to:', newQty);
    }

    /**
     * Get current cart ID - prefer cached ID from our last response, then SDK
     */
    getCartId() {

        // return the localStorage cart ID if it exists
        const localStorageCartId = localStorage.getItem('bqCartId');
        if (localStorageCartId) {
            console.log('[BooqableAddToCart] Using localStorage cart ID:', localStorageCartId);
            return localStorageCartId;
        }
        // Priority: 1) Our cached cart ID (most reliable), 2) SDK cartData.id, 3) SDK cartData.cartId
        if (this.cachedCartId) {
            console.log('[BooqableAddToCart] Using cached cart ID:', this.cachedCartId);
            return this.cachedCartId;
        }
        const sdkId = window.Booqable?.cartData?.id || window.Booqable?.cartData?.cartId || null;
        console.log('[BooqableAddToCart] Using SDK cart ID:', sdkId);
        return sdkId;
    }

    /**
     * Get storefront token
     */
    getToken() {
        const meta = document.querySelector('meta[name="booqable-storefront-token"]');
        if (meta?.content) return meta.content;
        return window.booqableOptions?.storefrontToken || window.BooqableConfig?.storefrontToken || '5e6ebf3d24ab26f8c300721e9ff8485e';
    }

    /**
     * Set loading state
     */
    setLoading(loading) {
        this.isLoading = loading;

        if (this.submitButton) {
            this.submitButton.disabled = loading;
            this.submitButton.classList.toggle('is-loading', loading);
        }

        // Disable quantity controls
        if (this.decreaseBtn) this.decreaseBtn.disabled = loading;
        if (this.increaseBtn) this.increaseBtn.disabled = loading;
        if (this.quantityInput) this.quantityInput.disabled = loading;
    }

    /**
     * Add item to Booqable cart
     */
    async addToCart() {
        if (this.isLoading || !this.productId) {
            console.log('[BooqableAddToCart] Blocked: isLoading=', this.isLoading, 'productId=', this.productId);
            return;
        }

        console.log('[BooqableAddToCart] Adding to cart:', {
            productId: this.productId,
            quantity: this.quantity
        });

        this.setLoading(true);

        try {
            const cartId = this.getCartId();

            // Snapshot existing line IDs so we can identify the line we're about
            // to add. We book using product-id, which may be a slug, but the API
            // normalizes it to the canonical product UUID in the response. The
            // cart drawer looks up min order quantity by that UUID, so we capture
            // it from the new line below and store the mapping under the UUID.
            const beforeLineIds = new Set(
                (window.Booqable?.cartData?.lines || []).map(function (l) { return l.item_id; })
            );

            const payload = {
                item_id: this.productId,
                quantity: this.quantity,
                configuration: { products: {} }
            };

            // Only include cart ID if we have one
            if (cartId) {
                payload.id = cartId;
            }

            console.log('[BooqableAddToCart] API Request:', {
                url: `${this.apiBaseUrl}/cart/book`,
                payload: payload
            });

            const response = await fetch(`${this.apiBaseUrl}/cart/book`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'x-booqable-store-provider': window.BooqableConfig?.provider || 'Shopify',
                    'x-booqable-storefront-token': this.getToken()
                },
                body: JSON.stringify(payload)
            });

            console.log('[BooqableAddToCart] API Response status:', response.status);

            if (response.ok) {
                const data = await response.json();
                console.log('[BooqableAddToCart] Success:', data);

                // IMPORTANT: Store the cart ID from response for subsequent adds
                if (data.cart?.id) {
                    this.cachedCartId = data.cart.id;
                    localStorage.setItem('bqCartId', data.cart.id);
                    console.log('[BooqableAddToCart] Updated cached cart ID:', this.cachedCartId);
                }

                // Backfill the min order quantity map keyed by the canonical
                // product UUID. Find the newly-added line (not present before the
                // add); its item_id is the UUID the cart drawer uses for lookup.
                const newLine = (data.cart?.lines || []).find(function (l) {
                    return !beforeLineIds.has(l.item_id);
                });
                if (newLine && newLine.item_id) {
                    this.saveMinOrderQuantity(newLine.item_id, this.minOrderQuantity);
                    console.log('[BooqableAddToCart] Stored min qty under UUID:', newLine.item_id, this.minOrderQuantity);
                }

                // Update Booqable SDK cart data
                if (window.Booqable.setCartData) {
                    window.Booqable.setCartData(data.cart);
                }

                // Dispatch success event
                this.dispatchEvent(new CustomEvent('booqable:added', {
                    bubbles: true,
                    detail: { cart: data.cart, quantity: this.quantity }
                }));

                // Check if cart has rental dates
                const hasRentalDates = data.cart?.starts_at && data.cart?.stops_at;

                if (!hasRentalDates) {
                    // Open date picker if no dates are set
                    const datePicker = document.querySelector('booqable-date-picker');
                    if (datePicker && datePicker.open) {
                        console.log('[BooqableAddToCart] No dates set, opening date picker');
                        datePicker.open();
                    }
                    // Also refresh cart drawer in background
                    const drawer = document.querySelector('booqable-cart-drawer');
                    if (drawer && drawer.refresh) {
                        await drawer.refresh();
                    }
                } else {
                    // Open cart drawer if dates are already set
                    const drawer = document.querySelector('booqable-cart-drawer');
                    if (drawer) {
                        if (drawer.refresh) await drawer.refresh();
                        if (drawer.open) drawer.open();
                    }
                }

                // Show success feedback
                this.showSuccessFeedback();

            } else {
                const error = await response.json().catch(function () { return {}; });
                console.error('[BooqableAddToCart] Failed to add item:', response.status, error);

                this.dispatchEvent(new CustomEvent('booqable:error', {
                    bubbles: true,
                    detail: { status: response.status, error: error }
                }));
            }

        } catch (error) {
            console.error('[BooqableAddToCart] Error:', error);

            this.dispatchEvent(new CustomEvent('booqable:error', {
                bubbles: true,
                detail: { error: error }
            }));

        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Save min order quantity to localStorage for use by cart drawer
     */
    saveMinOrderQuantity(productId, minQty) {
        if (!productId) return;
        try {
            var stored = JSON.parse(localStorage.getItem('bqMinOrderQty') || '{}');
            stored[productId] = minQty;
            localStorage.setItem('bqMinOrderQty', JSON.stringify(stored));
        } catch (e) { /* ignore */ }
    }

    /**
     * Show success feedback on button
     */
    showSuccessFeedback() {
        if (!this.submitButton) return;

        const originalText = this.submitButton.innerHTML;
        this.submitButton.innerHTML = '<span>✓ Added to Cart</span>';
        this.submitButton.classList.add('is-success');

        const btn = this.submitButton;
        setTimeout(function () {
            btn.innerHTML = originalText;
            btn.classList.remove('is-success');
        }, 2000);
    }
}

// Register custom element
if (!customElements.get('booqable-add-to-cart')) {
    customElements.define('booqable-add-to-cart', BooqableAddToCart);
    console.log('[BooqableAddToCart] Custom element registered');
}
